import { Helmet } from "react-helmet-async";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Container,
  Link,
  Grid,
  List,
  ListItem,
} from "@mui/material";

import Logo from "../components/Logo";
import SimpleFooter from "../components/SimpleFooter";

const StyledPageContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
});

const ContentWrapper = styled("div")(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8),
}));

const sections = [
  {
    id: "interpretation",
    title: "1. Interpretation and Definitions",
    content: (
      <>
        <Typography
          variant="h6"
          component="h3"
          sx={{ fontWeight: 600, mt: 2, mb: 1 }}
        >
          Interpretation
        </Typography>
        <Typography variant="body1" paragraph>
          Words with the first letter capitalized have specific meanings as
          defined below. The following definitions shall have the same meaning
          regardless of whether they appear in singular or in plural.
        </Typography>
        <Typography
          variant="h6"
          component="h3"
          sx={{ fontWeight: 600, mt: 3, mb: 1 }}
        >
          Definitions
        </Typography>
        <Typography variant="body1" paragraph>
          For the purposes of these Terms of Service:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <Typography variant="body1" paragraph>
            <strong>Account</strong> – a unique account created by You to access
            Contactie
            <Box
              component="sup"
              sx={{ fontSize: "0.6em", verticalAlign: "super" }}
            >
              TM
            </Box>{" "}
            or parts of its Service.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Company</strong> – refers to Contactie
            <Box
              component="sup"
              sx={{ fontSize: "0.6em", verticalAlign: "super" }}
            >
              TM
            </Box>
            , based in Tunisia, also referred to as "We", "Us", or "Our".
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Content</strong> – any text, images, CVs, job descriptions,
            or other information posted, uploaded, linked to, or made available
            by You.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Service</strong> – the Contactie
            <Box
              component="sup"
              sx={{ fontSize: "0.6em", verticalAlign: "super" }}
            >
              TM
            </Box>{" "}
            website, platform, AI tools, and physical NFC products.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Trust Score</strong> – the dynamic reputation score
            calculated by our proprietary Recency Algorithm based on verified
            ratings.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Terms</strong> – these Terms of Service.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>You</strong> – the individual accessing or using the
            Service, or the legal entity on behalf of which such individual is
            accessing the Service.
          </Typography>
        </Box>
      </>
    ),
  },
  {
    id: "acknowledgment",
    title: "2. Acknowledgment & Eligibility",
    content: (
      <>
        <Typography variant="body1" paragraph>
          By accessing or using the Service, You agree to be bound by these
          Terms. If You do not agree, do not use the Service.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Age Requirement:</strong> The Service is intended for
          Professionals and <strong>University Students</strong>.
        </Typography>
        <Typography variant="body1" paragraph>
          By using the Service, You represent that you are at least 18 years of
          age. If You are a University Student under the age of 18, You
          represent and warrant that you possess legal parental or guardian
          consent to use this Service and to purchase hardware products.
        </Typography>
        <Typography variant="body1" paragraph>
          Your access and use are also governed by our Privacy Policy, which
          explains how we collect, use, and protect your personal information in
          accordance with the Law.
        </Typography>
      </>
    ),
  },
  {
    id: "beta-status",
    title: "3. Beta Status & Purchases",
    content: (
      <>
        <Typography variant="body1" paragraph>
          The <strong>Digital Platform</strong> (Profile hosting, Basic AI) is
          currently free during the beta stage.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Hardware Purchases:</strong> Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>{" "}
          offers physical goods (e.g., NFC Cards, Keychains). These are paid
          products separate from the free digital service.
        </Typography>
        <Typography variant="body1" paragraph>
          Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>{" "}
          reserves the right to introduce paid subscriptions, goods, or services
          in the future.
        </Typography>
        <Typography variant="body1" paragraph>
          Features may change, be removed, or be unstable during the beta. By
          using the Service, You acknowledge and accept that it is provided
          “as-is”, and We are not liable for bugs, errors, or data loss.
        </Typography>
      </>
    ),
  },
  {
    id: "hardware-delivery",
    title: "4. Hardware Orders & Delivery (Cash on Delivery)",
    content: (
      <>
        <Typography variant="body1" paragraph>
          <strong>Binding Contract:</strong> By placing an order for physical
          hardware (NFC Cards), You enter into a binding contract to purchase
          the item.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Cash on Delivery:</strong> We operate on a "Cash on Delivery"
          basis for orders within Tunisia. You agree to be physically present at
          the provided address to complete the payment upon delivery.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Refusal of Delivery:</strong> You acknowledge that we incur
          shipping and manufacturing costs upon your order. Unjustified refusal
          to accept and pay for a confirmed order constitutes a breach of these
          Terms and may result in the suspension or permanent banning of Your
          Account.
        </Typography>
      </>
    ),
  },
  {
    id: "reputation-system",
    title: "5. Reputation System & Rating Integrity",
    content: (
      <>
        <Typography variant="body1" paragraph>
          <strong>The Trust Protocol:</strong> You acknowledge that the
          integrity of the "Trust Score" is the core value of the Platform. Our
          Recency Algorithm prioritizes current performance over past history.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Irreversibility of Ratings:</strong> To ensure transparency
          for recruiters and clients, You agree that verified ratings received
          from third parties
          <strong> cannot be deleted, hidden, or altered by You</strong>, even
          if they are negative, provided they comply with our Community
          Guidelines (e.g., they do not contain hate speech).
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Anti-Manipulation:</strong> Any attempt to artificially
          inflate your score (e.g., creating fake accounts, self-rating, trading
          ratings) is strictly prohibited and will result in immediate Account
          termination.
        </Typography>
      </>
    ),
  },
  {
    id: "user-accounts",
    title: "6. User Accounts & Information",
    content: (
      <>
        <Typography variant="body1" paragraph>
          When creating an Account, You must provide accurate, complete, and
          current information. Failure to do so constitutes a breach of the
          Terms.
        </Typography>
        <Typography variant="body1" paragraph>
          Required information includes: first and last name, email, and phone
          number.
        </Typography>
        <Typography variant="body1" paragraph>
          You are responsible for safeguarding your password and all activity
          under your Account.
        </Typography>
        <Typography variant="body1" paragraph>
          Notify Us immediately upon becoming aware of any breach of security or
          unauthorized use of Your Account.
        </Typography>
      </>
    ),
  },
  {
    id: "public-private-data",
    title: "7. Public & Private Data",
    content: (
      <>
        <Typography variant="body1" paragraph>
          Required data (email, phone, password) is private and will be
          protected in accordance with our Privacy Policy.
        </Typography>
        <Typography variant="body1" paragraph>
          Optional data (social media links, profile pictures, job titles,
          biography) is considered <strong>Public Information</strong>.
        </Typography>
        <Typography variant="body1" paragraph>
          By posting such Content, You explicitly consent to it being displayed
          publicly, indexed by search engines, and viewed by other users or
          recruiters.
        </Typography>
        <Typography variant="body1" paragraph>
          We are not responsible for how public data is viewed or used by third
          parties outside of our Platform.
        </Typography>
      </>
    ),
  },
  {
    id: "ai-tools",
    title: "8. AI Tools & Fit Analyzer",
    content: (
      <>
        <Typography variant="body1" paragraph>
          The Service includes AI-powered tools (e.g., "Fit Analyzer") utilizing
          Google Gemini technology.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>No Guarantee:</strong> Results generated by AI are for
          informational purposes only. Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>
          does not guarantee that a high "Fit Score" will result in employment.
        </Typography>
        <Typography variant="body1" paragraph>
          You agree not to upload sensitive personal documents (CVs/Resumes)
          that you do not own or have explicit permission to use.
        </Typography>
      </>
    ),
  },
  {
    id: "content-posting",
    title: "9. Content Posting & Responsibility",
    content: (
      <>
        <Typography variant="body1" paragraph>
          You are solely responsible for all Content You post.
        </Typography>
        <Typography variant="body1" paragraph>
          You may not post Content that is unlawful, defamatory, offensive,
          spam, or infringing on others’ intellectual property rights.
        </Typography>
        <Typography variant="body1" paragraph>
          We reserve the right to remove Content that violates these Terms, but
          we are not responsible for reviewing all user Content.
        </Typography>
        <Typography variant="body1" paragraph>
          You should maintain your own copy of Content, as We do not guarantee
          backups or permanent storage of your data.
        </Typography>
      </>
    ),
  },
  {
    id: "intellectual-property",
    title: "10. Intellectual Property",
    content: (
      <>
        <Typography variant="body1" paragraph>
          Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>{" "}
          and its original content (excluding Content provided by You or other
          users), features, and functionality are and will remain the exclusive
          property of the Company.
        </Typography>
        <Typography variant="body1" paragraph>
          Specifically, the <strong>Recency Algorithm</strong> and the logic
          behind the "Trust Score" calculation are proprietary trade secrets of
          the Company.
        </Typography>
        <Typography variant="body1" paragraph>
          Users retain ownership of their Content but grant Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>{" "}
          a worldwide, royalty-free license to use, display, and distribute it
          within the Service.
        </Typography>
        <Typography variant="body1" paragraph>
          We respect the intellectual property rights of others. If you believe
          Content on the Service infringes on a copyright, please submit your
          claim via email to{" "}
          <Link href="mailto:contact@contactie.com">contact@contactie.com</Link>{" "}
          with the subject line: "Copyright Infringement".
        </Typography>
      </>
    ),
  },
  {
    id: "termination",
    title: "11. Termination",
    content: (
      <>
        <Typography variant="body1" paragraph>
          We may terminate or suspend Your Account immediately, without prior
          notice or liability, for any reason whatsoever, including without
          limitation if You breach these Terms.
        </Typography>
        <Typography variant="body1" paragraph>
          Upon termination, your right to use the Service will cease
          immediately.
        </Typography>
      </>
    ),
  },
  {
    id: "liability-limitation",
    title: "12. Limitation of Liability",
    content: (
      <>
        <Typography
          variant="body1"
          paragraph
          component="div"
          sx={{ textTransform: "uppercase", fontWeight: "bold" }}
        >
          THE SERVICE IS PROVIDED TO YOU "AS IS" AND "AS AVAILABLE" WITHOUT
          WARRANTY OF ANY KIND. Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>
          , ON ITS OWN BEHALF AND ON BEHALF OF ITS OWNERS, EXPRESSLY DISCLAIMS
          ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR OTHERWISE.
        </Typography>
        <Typography
          variant="body1"
          paragraph
          component="div"
          sx={{ textTransform: "uppercase", fontWeight: "bold", mt: 2 }}
        >
          IN NO EVENT SHALL Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>{" "}
          OR ITS OWNERS BE LIABLE FOR ANY SPECIAL, INDIRECT, OR CONSEQUENTIAL
          DAMAGES WHATSOEVER. TO THE MAXIMUM EXTENT PERMITTED BY TUNISIAN LAW,
          THE TOTAL LIABILITY OF THE COMPANY AND ITS OWNERS SHALL NOT EXCEED THE
          AMOUNT PAID BY YOU (IF ANY), OR ZERO TUNISIAN DINARS (0 TND) IF THE
          SERVICE WAS FREE.
        </Typography>
      </>
    ),
  },
  {
    id: "indemnification",
    title: "13. Indemnification",
    content: (
      <Typography variant="body1" paragraph>
        You agree to defend, indemnify, and hold harmless the Company and its
        owners, agents, and affiliates from and against any and all claims,
        damages, liabilities, and expenses (including attorney's fees) arising
        from: (a) Your use of the Service; (b) Your violation of these Terms; or
        (c) Content You post on the Service.
      </Typography>
    ),
  },
  {
    id: "governing-law",
    title: "14. Governing Law & Disputes",
    content: (
      <>
        <Typography variant="body1" paragraph>
          These Terms are governed by the laws of <strong>Tunisia</strong>.
        </Typography>
        <Typography variant="body1" paragraph>
          You agree that any legal action or proceeding arising out of these
          Terms shall be brought exclusively in the courts located in Tunisia.
        </Typography>
      </>
    ),
  },
  {
    id: "changes-to-terms",
    title: "15. Changes to Terms",
    content: (
      <>
        <Typography variant="body1" paragraph>
          We reserve the right, at Our sole discretion, to modify or replace
          these Terms at any time.
        </Typography>
        <Typography variant="body1" paragraph>
          Continued use of the Service after updates constitutes agreement to
          the new Terms. If You do not agree to the new terms, please stop using
          the Service.
        </Typography>
      </>
    ),
  },
  {
    id: "contact",
    title: "16. Contact Us",
    content: (
      <Typography variant="body1" paragraph>
        If you have any questions about these Terms, please contact us at:{" "}
        <Link href="mailto:contact@contactie.com">contact@contactie.com</Link>
      </Typography>
    ),
  },
];

export default function TermsOfUsePage() {
  const theme = useTheme();

  return (
    <>
      <Helmet>
        <title>Terms of Service (Beta) | Contactie™</title>
        <meta
          name="description"
          content="Official Beta Terms of Service for the Contactie™ platform. By using our service, you agree to these terms."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <StyledPageContainer>
        <Box
          component="header"
          sx={{ py: 3, px: 5, display: { xs: "none", md: "flex" } }}
        >
          <Logo sx={{ height: 30, width: "auto" }} />
        </Box>

        <ContentWrapper>
          <Container maxWidth="lg">
            <Box sx={{ mb: 5, textAlign: { xs: "center", md: "left" } }}>
              <Box
                sx={{
                  mb: 5,
                  display: { xs: "flex", md: "none" },
                  justifyContent: "center",
                }}
              >
                <Logo sx={{ height: 35, width: "auto" }} />
              </Box>
              <Typography variant="h2" component="h1" sx={{ fontWeight: 700 }}>
                Contactie
                <Box
                  component="sup"
                  sx={{ fontSize: "0.6em", verticalAlign: "super" }}
                >
                  TM
                </Box>{" "}
                Terms of Service (Beta)
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Last Updated:{" "}
                {new Date().toLocaleString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>
            </Box>

            <Grid container spacing={5}>
              <Grid item xs={12} md={8}>
                {sections.map((section) => (
                  <Box key={section.id} id={section.id} sx={{ mb: 5 }}>
                    <Typography
                      variant="h4"
                      component="h2"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {section.title}
                    </Typography>
                    {section.content}
                  </Box>
                ))}
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    position: "sticky",
                    top: theme.spacing(4),
                    display: { xs: "none", md: "block" },
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    On this page
                  </Typography>
                  <List dense>
                    {sections.map((section) => (
                      <ListItem key={section.id} disablePadding>
                        <Link
                          href={`#${section.id}`}
                          underline="hover"
                          sx={{ color: "text.secondary", py: 0.5 }}
                        >
                          {section.title}
                        </Link>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </ContentWrapper>
        <SimpleFooter />
      </StyledPageContainer>
    </>
  );
}
